// ============================================================================
// TRIMA STORAGE — Encrypted localStorage wrapper
// ============================================================================
// Persists ONLY cadran-phase state (currentCadran, currentSelections,
// passageNumber, completedCadrans). Phase 1 (ranking) is intentionally NOT
// persisted: if the user quits during ranking, they restart from invitation.
//
// All API surface goes through this file. To remove Trima persistence later
// (or to swap implementations e.g. IndexedDB), only this file needs editing.
// ============================================================================

import { TRIMA_STORAGE_KEY } from 'pages/questionnaire-trima/constants/trima.constants';
import { TrimaPersistedState } from 'pages/questionnaire-trima/types/trima.types';
import { trimaEncrypt, trimaDecrypt } from 'helpers/trima/trimaEncryption';

export const trimaStorage = {
  /**
   * Read the persisted state, or null if nothing saved / corrupted / decryption
   * failed.
   */
  load(): TrimaPersistedState | null {
    try {
      const raw = localStorage.getItem(TRIMA_STORAGE_KEY);
      if (!raw) return null;
      const decrypted = trimaDecrypt(raw);
      if (!decrypted) return null;
      const parsed = JSON.parse(decrypted) as TrimaPersistedState;
      // Light validation — if the shape doesn't match, treat as no state.
      if (
        typeof parsed.currentCadran !== 'number' ||
        !Array.isArray(parsed.currentSelections) ||
        !Array.isArray(parsed.completedCadrans)
      ) {
        return null;
      }
      return parsed;
    } catch (e) {
      console.error('[trimaStorage.load] failed:', e);
      return null;
    }
  },

  /**
   * Write the state (overwrites previous). No-op on encryption failure.
   */
  save(state: TrimaPersistedState): void {
    try {
      const stateWithTimestamp: TrimaPersistedState = {
        ...state,
        savedAt: Date.now(),
      };
      const encrypted = trimaEncrypt(JSON.stringify(stateWithTimestamp));
      if (encrypted) {
        localStorage.setItem(TRIMA_STORAGE_KEY, encrypted);
      }
    } catch (e) {
      console.error('[trimaStorage.save] failed:', e);
    }
  },

  /**
   * Clear all Trima state. Called when:
   * - The user refuses the invitation
   * - The parcours is completed (after THANKS screen)
   * - Manual reset (admin / debug)
   */
  clear(): void {
    try {
      localStorage.removeItem(TRIMA_STORAGE_KEY);
    } catch (e) {
      console.error('[trimaStorage.clear] failed:', e);
    }
  },

  /**
   * Convenience: is there any Trima state persisted right now?
   */
  hasState(): boolean {
    return localStorage.getItem(TRIMA_STORAGE_KEY) !== null;
  },
};
